import { useEffect, useState } from 'react';
import { useAnalyticsSelection, type AnalyticsSelection } from '@/modules/analytics-v2/use-analytics-selection';
import type { SentinelChannel } from '@/types/sentinelAnalytics';

interface SentinelFilters {
  /** Picked filter values as the menus list them; empty means no filter. */
  channels: SentinelChannel[];
  categories: string[];
  subCategories: string[];
}

export type SentinelAnalyticsSelection = AnalyticsSelection & SentinelFilters;

const STORAGE_KEY = 'sentinel-analytics-filters';

const NO_FILTERS: SentinelFilters = { channels: [], categories: [], subCategories: [] };

/** Filters remember the account they were picked for and apply only to it, as the account can change on an Internal page. */
type StoredFilters = SentinelFilters & { accountId: string | null };

/** The shared account, brand and range, plus Sentinel's own filters kept for the browser tab's session. */
export function useSentinelAnalyticsSelection() {
  const [shared, setShared] = useAnalyticsSelection();
  const [stored, setStored] = useState<StoredFilters>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return { ...NO_FILTERS, accountId: null, ...(saved ? (JSON.parse(saved) as Partial<StoredFilters>) : {}) };
    } catch {
      return { ...NO_FILTERS, accountId: null };
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // storage unavailable: the filters just aren't kept
    }
  }, [stored]);

  const { channels, categories, subCategories } = stored.accountId === (shared.account?.id ?? null) ? stored : NO_FILTERS;
  const selection: SentinelAnalyticsSelection = { ...shared, channels, categories, subCategories };
  const setSelection = (update: (current: SentinelAnalyticsSelection) => SentinelAnalyticsSelection) => {
    const { account, buId, startDate, endDate, channels, categories, subCategories } = update(selection);
    setShared({ account, buId, startDate, endDate });
    setStored({ channels, categories, subCategories, accountId: account?.id ?? null });
  };

  return [selection, setSelection] as const;
}
